import { ApiResponse } from 'apisauce';
import { IResourceAvailableTagsResponse } from 'app/components/ResourceSubmission/hooks/useResourceAvailableTags';
import { IResourceSubmissionFieldsResponse } from 'app/components/ResourceSubmission/hooks/useResourceSubmissionFields';
import { IResource } from 'app/components/Resources/ResourceCard';
import { IMyResourcesResponse } from 'app/components/Resources/hooks/useMyResources';
import { IResourcesFiltersResponse } from 'app/components/Resources/hooks/useResourcesFiltersService';
import { IResourceIndexResponse } from 'app/components/Resources/interfaces';
import {
  IResourcesFilters,
  RESOURCES_FILTER_TYPE,
} from 'store/reducers/resources/resources-reducer';

import { http } from './http';

export const getResources = (
  params: IResourcesFilters & {
    groupings?: { uuid: string; type: string }[];
    per_page?: number;
  } = {
    per_page: 9,
  }
) => {
  const filtersMap = new Map<string, string>([
    [RESOURCES_FILTER_TYPE.RESOURCE_TYPES, 'types'],
    [RESOURCES_FILTER_TYPE.INDUSTRIES, 'industries'],
    [RESOURCES_FILTER_TYPE.CLIENTS, 'clients'],
    [RESOURCES_FILTER_TYPE.PASSIONS, 'passions'],
    [RESOURCES_FILTER_TYPE.CURRENT_COMPANY, 'companies'],
    [RESOURCES_FILTER_TYPE.FORMATS, 'formats'],
    [RESOURCES_FILTER_TYPE.TAGS, 'tags'],
    [RESOURCES_FILTER_TYPE.RESOURCES, 'resources'],
  ]);

  const filters = Object.keys(params).reduce((acc, key) => {
    if (filtersMap.has(key)) {
      acc[filtersMap.get(key)] = params[key].join(';');
    }
    return acc;
  }, {});

  return http.post<IResourceIndexResponse>(`resources`, {
    ...filters,
    groupings: params.groupings,
  });
};

export const getResourcesFilters = (params: any) => {
  return http.get<IResourcesFiltersResponse>(`resources/filters`, params);
};

export const getResource = (slug: string): Promise<ApiResponse<IResource, IResource>> => {
  return http.get<IResource>(`resources/${slug}`);
};

export type ResourceEmbedStatus =
  | 'embeddable'
  | 'blocked'
  | 'unavailable'
  | 'unsafe_url'
  | 'not_checked';

export const getResourceEmbedStatus = (
  slug: string
): Promise<ApiResponse<{ status: ResourceEmbedStatus }, { status?: ResourceEmbedStatus }>> => {
  return http.get<{ status: ResourceEmbedStatus }>(`resources/${slug}/embed_status`);
};

export const getResourceMessages = (slug: string): Promise<ApiResponse<any, any>> => {
  return http.get<any>(`resources/${slug}/messages`);
};

export const getMyResources = () => {
  return http.get<IMyResourcesResponse>('resources/my_resources');
};

export const getResourceSubmissionFields = (bestOfYear?: number) => {
  return http.get<IResourceSubmissionFieldsResponse>(
    'resources/submission_fields',
    bestOfYear ? { best_of: bestOfYear } : undefined
  );
};

export const getResourceValidate = (data) => {
  return http.get<any>('resources/validate', data);
};

export const getResourceAvailableTags = () => {
  return http.get<IResourceAvailableTagsResponse>('resources/available_tags');
};

export const postResource = (data): Promise<ApiResponse<any, any>> => {
  return http.post('resources/submission', data);
};

export const getResourceVoting = () => {
  return http.get('resources/voting_index');
};

export const postResourceVote = (slug: string) => {
  return http.post(`resources/${slug}/vote`);
};

export const getResourceWinners = () => {
  return http.get('resources/winners');
};

export const getResourceSamples = (uuids: string[]) => {
  return http.post<any>('resources/samples', { uuids });
};

export const getResourceProjectCategories = () => {
  return http.get('resources/project_categories');
};

export const createProjectCategory = (data) => {
  return http.post('resources/new_project_category', data);
};
